'use client'

import Logo from '@/assets/logo.svg'
import { formatImageURL } from '@/utils/util'
import Image from 'next/image'
import { Mail, Phone } from 'lucide-react'

export default function Maintenance({ vendor }: { vendor: any }) {
  const contactItems = [
    {
      icon: Mail,
      label: "Имэйл",
      value: vendor?.email,
      href: `mailto:${vendor?.email}`,
    },
    {
      icon: Phone,
      label: "Утас",
      value: vendor?.phone,
      href: `tel:${vendor?.phone}`,
    },
  ].filter(item => item.value)

  return (
    <div className="relative flex items-center justify-center min-h-screen bg-white">
      <div className="absolute top-0 left-1/2 -translate-x-1/2 max-w-2xl w-full h-36 flex items-start justify-center pt-12">
        <div aria-hidden="true" className="absolute inset-0">
          <div className="absolute top-[35%] left-[30%] -translate-x-1/2 -translate-y-1/2 w-[60%] h-[80%] rounded-full bg-[#4F6AF1] opacity-30 blur-[60px]" />
          <div className="absolute top-1/2 left-[58%] -translate-x-1/2 -translate-y-1/2 w-[80%] h-[120%] rounded-full opacity-15 blur-[60px]" style={{ background: 'linear-gradient(180deg, #FF57EB 0%, #F8D000 100%)' }} />
        </div>
        <Image src={Logo} alt="Shop.mn" height={48} className="relative h-8 md:h-10 xl:h-12 w-auto" />
      </div>
      <div className="w-full self-stretch flex flex-col justify-end mt-[35vh]">
        <div className="relative mx-4 md:mx-12 lg:mx-24 flex-1 flex flex-col items-center z-10 px-4">
          <div className="absolute inset-0 rounded-t-[64px] overflow-hidden">
            <div aria-hidden="true" className="absolute inset-0 -z-10 overflow-hidden" style={{ maskImage: 'linear-gradient(to bottom, black 60%, transparent 95%), linear-gradient(to right, transparent 0%, black 3%, black 97%, transparent 100%)', maskComposite: 'intersect', WebkitMaskImage: 'linear-gradient(to bottom, black 60%, transparent 95%), linear-gradient(to right, transparent 0%, black 3%, black 97%, transparent 100%)', WebkitMaskComposite: 'source-in' }}>
              <div className="absolute top-[-40%] inset-x-0 w-full h-[140%] bg-[#B8BBF5]/30 rounded-full blur-[150px]" />
              <div className="absolute top-[5%] left-[30%] w-[24%] h-[48%] rounded-[295px] opacity-16 blur-[126px]" style={{ background: 'linear-gradient(180deg, #9B7AF1 0%, #FF57EB 60%, #F8D000 100%)' }} />
              <div className="absolute top-[4%] left-[30%] w-[22%] h-[45%] rounded-full opacity-20 blur-[125px]" style={{ background: 'linear-gradient(180deg, #FF57EB 0%, #F8D000 100%)' }} />
              <div className="absolute top-[25%] left-[28%] w-[22%] h-[55%] rounded-[295px] bg-[#5AFF97] opacity-16 blur-[126px] rotate-[-22deg]" />
              <div className="absolute top-[30%] left-[58%] w-[24%] h-[48%] rounded-[295px] bg-[#6366F1] opacity-16 blur-[126px]" />
            </div>
          </div>
          <div className="absolute inset-0 rounded-t-[62px] pointer-events-none opacity-80 -z-10" style={{ background: 'linear-gradient(180deg, rgba(250, 250, 250, 0.00) 0%, #FAFAFA 100%)', backdropFilter: 'blur(17.7px)', WebkitBackdropFilter: 'blur(17.7px)' }} />
          <div className="absolute inset-0.5 rounded-t-[62px] pointer-events-none p-0.5 pb-0 shadow-[0_1px_2px_rgba(0,0,0,0.05)]" style={{ background: 'linear-gradient(to bottom, rgba(63,63,70,0.32), rgba(63,63,70,0.12) 3%, rgba(63,63,70,0) 100%)', WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)', WebkitMaskComposite: 'xor', maskComposite: 'exclude' }} />
          <div className="relative -mt-18 md:-mt-24 xl:-mt-30 rounded-[40px] p-1 mb-12 md:mb-16 xl:mb-21 size-40 md:size-48 xl:size-60 flex items-center justify-center bg-zinc-200 border border-zinc-300">
            <div className="w-full h-full rounded-[36px] border border-zinc-300 bg-white flex items-center justify-center px-2">
              {vendor?.logo ? (
                <img src={formatImageURL(vendor?.logo)} alt="Vendor Logo" className="max-h-16 md:max-h-24 xl:max-h-32 max-w-24 md:max-w-32 xl:max-w-48 w-auto shrink-0" />
              ) : (
                <div className="text-2xl md:text-3xl xl:text-4xl font-bold tracking-tight text-center">{vendor?.name}</div>
              )}
            </div>
          </div>
          <h1 className="text-3xl lg:text-4xl xl:text-[44px] font-gilroy font-semibold tracking-[-0.6%] text-center mb-2 sm:mb-3">Түр засвартай</h1>
          <p className="text-muted-foreground font-medium font-gilroy text-lg lg:text-xl xl:text-2xl tracking-[-0.6%] leading-8 text-center mb-8 max-w-xl">
            {vendor?.name ? `${vendor.name} дэлгүүр нь түр хугацаанд засвар үйлчилгээнд орсон байна.` : 'Дэлгүүр түр хугацаанд засвар үйлчилгээнд орсон байна.'}
          </p>
          {contactItems.length > 0 && (
            <div className="flex items-center justify-center gap-4 sm:gap-6 z-20">
              {contactItems.map((item, index) => {
                const IconComponent = item.icon
                return (
                  <a key={index} href={item.href} className="flex items-center gap-2.5 px-4 py-2 rounded-md border border-border bg-white hover:bg-zinc-50 transition-colors">
                    <IconComponent className="w-5 h-5 text-muted-foreground" />
                    <span className="text-sm sm:text-base font-medium text-foreground">{item.value}</span>
                  </a>
                )
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
